import axios from 'axios';
import { getUserId } from './userUtils';

// Subscription API endpoint
const SUBSCRIPTION_API_URL = 'https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions';

// Cache for subscription data to avoid repeated API calls
let subscriptionCache = null;
let cacheExpiry = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch user's subscription data from the API
 * @param {Object} user - AWS Amplify user object
 * @returns {Promise<Object>} Subscription data
 */
export const fetchUserSubscription = async (user) => {
  try {
    const userId = getUserId(user);
    if (!userId) {
      console.error('No user ID available for subscription check');
      return null;
    }

    // Check cache first
    const now = Date.now();
    if (subscriptionCache && now < cacheExpiry) {
      console.log('Using cached subscription data');
      return subscriptionCache;
    }

    console.log('Fetching subscription data for user:', userId);
    const response = await axios.get(`${SUBSCRIPTION_API_URL}/${userId}`);
    
    // Cache the result
    subscriptionCache = response.data;
    cacheExpiry = now + CACHE_DURATION;
    
    console.log('Subscription data fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    
    // Clear cache on error
    subscriptionCache = null;
    cacheExpiry = 0;
    
    // Return null to indicate no subscription
    return null;
  }
};

/**
 * Get user's subscription tier (free, basic, or pro)
 * @param {Object} user - AWS Amplify user object
 * @returns {Promise<string>} Subscription tier ("free", "0" for basic, "1" for pro)
 */
export const getSubscriptionTier = async (user) => {
  try {
    const subscription = await fetchUserSubscription(user);
    
    if (!subscription) {
      console.log('No subscription data found - defaulting to free');
      return 'free';
    }

    const tier = subscription.subscription_tier || 'free';
    console.log('User subscription tier:', tier);
    return tier;
  } catch (error) {
    console.error('Error getting subscription tier:', error);
    return 'free';
  }
};

/**
 * Check if user has an active paid subscription (basic or pro)
 * @param {Object} user - AWS Amplify user object
 * @returns {Promise<boolean>} Whether user has active paid subscription
 */
export const hasActivePaidSubscription = async (user) => {
  try {
    const subscription = await fetchUserSubscription(user);
    
    if (!subscription) {
      console.log('No subscription data found');
      return false;
    }

    // Check if subscription is active and not free
    const isActivePaidSubscription = 
      subscription.subscription_status === "active" && 
      subscription.subscription_tier !== "free";

    console.log('Paid subscription check:', {
      tier: subscription.subscription_tier,
      status: subscription.subscription_status,
      isActivePaidSubscription
    });

    return isActivePaidSubscription;
  } catch (error) {
    console.error('Error checking paid subscription:', error);
    return false;
  }
};

/**
 * Legacy function for backward compatibility
 * @param {Object} user - AWS Amplify user object
 * @returns {Promise<boolean>} Whether user has active subscription
 */
export const hasActiveSamSubscription = async (user) => {
  return await hasActivePaidSubscription(user);
};

/**
 * Check if user can send messages to any character
 * Three-tier system: Free users can chat, Basic/Pro users get additional features
 * @param {Object} user - AWS Amplify user object
 * @param {string} botId - Character/bot ID
 * @returns {Promise<boolean>} Whether user can send messages
 */
export const canSendMessages = async (user, botId = null) => {
  if (!user) {
    console.log('No user - cannot send messages');
    return false;
  }

  // In three-tier system, all signed users can send messages
  // The difference is in media access, not chat access
  console.log(`Message sending permission for bot ${botId}: true (signed user)`);
  return true;
};

/**
 * Check if user can access chat (read-only) with a character
 * Logged-in users can access chats but need subscription to send messages
 * @param {Object} user - AWS Amplify user object
 * @param {string} botId - Character/bot ID
 * @returns {boolean} Whether user can access chat
 */
export const canAccessChat = (user, botId = null) => {
  // All logged-in users can access chats (read-only)
  const canAccess = !!user;
  console.log(`Chat access permission for bot ${botId}:`, canAccess);
  return canAccess;
};

/**
 * Get subscription status message for UI display
 * @param {Object} user - AWS Amplify user object
 * @param {string} botId - Character/bot ID
 * @returns {Promise<Object>} Status object with message and type
 */
export const getSubscriptionStatus = async (user, botId = null) => {
  if (!user) {
    return {
      message: "Please log in to chat with characters",
      type: "login_required",
      canSendMessages: false,
      canAccessChat: false
    };
  }

  const canAccess = canAccessChat(user, botId);
  const canSend = await canSendMessages(user, botId);
  const tier = await getSubscriptionTier(user);

  if (tier === 'free') {
    return {
      message: "Free Plan - Chat access, upgrade for media features",
      type: "free_tier",
      canSendMessages: true,
      canAccessChat: true,
      tier: 'free'
    };
  }

  if (tier === '0') {
    return {
      message: "Basic Plan - Chat + limited media access",
      type: "basic_tier",
      canSendMessages: true,
      canAccessChat: true,
      tier: '0'
    };
  }

  if (tier === '1') {
    return {
      message: "Pro Plan - Full access with higher media limits",
      type: "pro_tier",
      canSendMessages: true,
      canAccessChat: true,
      tier: '1'
    };
  }

  return {
    message: "Please log in to access chats",
    type: "login_required",
    canSendMessages: false,
    canAccessChat: false
  };
};

/**
 * Clear subscription cache (useful for testing or after subscription changes)
 */
export const clearSubscriptionCache = () => {
  subscriptionCache = null;
  cacheExpiry = 0;
  console.log('Subscription cache cleared');
};

/**
 * Get subscription upgrade URL
 * @param {Object} user - AWS Amplify user object
 * @returns {string} URL for subscription management
 */
export const getSubscriptionUpgradeUrl = (user) => {
  // This would typically link to a subscription purchase flow
  // For now, linking to subscription management
  return '/subscription-management';
};

/**
 * Check if a bot/character requires subscription for chat access
 * @param {string} botId - Character/bot ID
 * @returns {boolean} Whether subscription is required for chat
 */
export const requiresSubscription = (botId) => {
  // In three-tier system, no characters require subscription for chat
  // All signed users can chat, subscription affects media features
  return false;
};

/**
 * Get upgrade path message based on current tier
 * @param {string} currentTier - Current subscription tier
 * @returns {Object} Upgrade information
 */
export const getUpgradePath = (currentTier) => {
  switch (currentTier) {
    case 'free':
      return {
        nextTier: 'Basic Plan',
        benefits: 'Get 10 pictures and 5 videos per month',
        upgradeUrl: '/subscription-management'
      };
    case '0':
      return {
        nextTier: 'Pro Plan',
        benefits: 'Get 100 pictures and 100 videos per month',
        upgradeUrl: '/subscription-management'
      };
    case '1':
      return {
        nextTier: null,
        benefits: 'You have the highest tier!',
        upgradeUrl: '/subscription-management'
      };
    default:
      return {
        nextTier: 'Basic Plan',
        benefits: 'Get media access with a subscription',
        upgradeUrl: '/subscription-management'
      };
  }
};